import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import useSWR from 'swr';
import { v4 as UUID } from 'uuid';
import _ from 'lodash';
import { ADMIN_ACCESS_TOKEN_KEY, fetcher, getFetcher, getWindowFetcher, SwButtonSection } from '../../../../App';
import { NotificationSetting, ExpandRecipient, Metatag, DicomTag } from '@models/strokeSetting/notification';
import { css } from '@emotion/react';
import { FetchResponse } from '@models/common';
import { useCatchLeave, useGeolocation } from 'utils/hooks';

import SettingCommonMethods from '../../../../utils/settingCommon';
import { submitDebounce } from '../../../../utils/lib';

import { PrimaryButton, Checkbox, Sub1Button, SelectSearchDicomTag, Select, SwitchAsync } from '@components/atoms';
import { dialogContentsState } from '@components/templates';
import Recipient from '@components/organisms/Recipient';
import ScheduleRecipient, { Staff } from '@components/organisms/ScheduleRecipient';

import {
  StyledNotificationWrap,
  StyledNotificationArea,
  StyledNotificationBox,
  StyledInputList,
  StyledExcelButton,
  StyledExcelRow,
  StyledNotificationBoxFlex,
} from './style';

const DISABLED_TARGETS = ['contentAcquisitionDate', 'contentAnalysisStartDate', 'contentAnalysisEndDate'];

const NotificationPage = () => {
  const { code } = useGeolocation();

  const ref = useRef<HTMLInputElement | null>(null);
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const [accessToken, setAccessToken] = useState('');
  const [settings, setSettings] = useState<NotificationSetting | null>(null); // noti데이터 전체
  const [backup, setBackup] = useState<NotificationSetting | null>(null); // noti데이터 전체
  const [recipients, setRecipients] = useState<ExpandRecipient[]>([]); // notificationRecipients데이터 + @
  const [recipientsBackup, setRecipientsBackup] = useState<ExpandRecipient[]>([]); // notificationRecipients데이터 + @
  const [dicomTagList, setDicomTagList] = useState<DicomTag[]>([]);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [staff, setStaff] = useState<Staff[]>([]);
  const [metaList, setMetaList] = useState<Metatag>({
    action: '',
    dataset: '',
    definition: '',
    dicomTagId: 0,
    method: '',
    tag: '',
  }); // metatag 단일 아이템

  const [disableds, setDisableds] = useState<{ [key: string]: boolean }>({
    contentAcquisitionTime: false,
    contentAnalysisStartTime: false,
    contentAnalysisEndTime: false,
  });

  const { data, error, mutate } = useSWR<FetchResponse<NotificationSetting>>(
    !!accessToken.length ? [{ url: 'api/setting/scs/notification', accessToken }] : null,
  );

  const { getDicomTagAll, errorHandler } = SettingCommonMethods();

  /**
   * backup, settings 의 값을 비교하여 바뀐 값이 있는지 확인
   */
  const isMutate = useMemo(() => {
    if (!backup || !settings) return false;

    // 수정중인 recipient
    const editingRecipients = recipients.filter(({ isEdit }) => isEdit);
    const notCompleteRecipients = recipients.filter(({ isComplete }) => !isComplete);

    // 현재 Edit 중이 거나 Complete 가 되지 않느 item 이 있는 경우 apply 비활성화
    if (editingRecipients.length > 0 || notCompleteRecipients.length > 0) return true;

    // 기본 setting 변경 사항 확인
    if (JSON.stringify(backup) !== JSON.stringify(settings)) return false;

    // backup recipient 와 현재 recipient 가 다를 경우 apply 활성화
    const originalRecipient = recipients.map(
      ({ active, countryCallingCode, designator, email, id, name, notificationId, oncall, tel }) => ({
        id,
        notificationId,
        active,
        designator,
        oncall,
        name,
        email,
        countryCallingCode,
        tel,
      }),
    );

    if (JSON.stringify(backup.notificationRecipients) !== JSON.stringify(originalRecipient)) return false;

    return true;
  }, [backup, settings, recipients]);

  // 값 변경이 있는 경우 새로고침, 탭닫기 막기
  useCatchLeave(!isMutate);

  /**
   * 체크박스 핸들러
   * Date, Time 의 경우 상태 체이닝 설정 필요. contentAcquisitionDate, contentImportDate, contentAnalysisDate
   */
  const checkedHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = ev.target;

    setSettings((prevState) => {
      if (!prevState) return null;
      return {
        ...prevState,
        [name]: checked,
      };
    });

    // disalbed 관리 대상의 경우 아래 로직 수행
    if (DISABLED_TARGETS.includes(name)) {
      // disabled 대상의 Date를 Time 으로 교체
      const disabledTarget = (name as string).split('Date')[0] + 'Time';

      // 해당 대상이 있는지 검사 후 상태에 맞게 변경
      if (disableds[disabledTarget] !== undefined) {
        setDisableds((prevState) => ({
          ...prevState,
          [disabledTarget]: checked === false,
        }));
      }
    }
  };

  /**
   * Recipient 컴포넌트에서 이루어지는 recipients 업데이트 사항 반영
   */
  const recipientsUpdateHandler = (recipients: ExpandRecipient[]) => {
    setRecipients(recipients);
  };

  /**
   * 엑셀 다운로드
   */
  const downloadExcelHandler = async () => {
    const url = `api/duty-schedule/excel?year=${year}&month=${month}`;

    const response = await getWindowFetcher({
      method: 'GET',
      accessToken,
    })(url);

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const parts = contentDisposition.split(';');
    const filename = parts[1].split('=')[1].replaceAll('"', '');
    const downloadPath = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.download = filename;
    a.href = downloadPath;
    a.click();
    a.remove();
  };

  /**
   * 엑셀 업로드
   */
  const uploadExcelHandler = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const files = ev.target.files;

    if (!files || files.length === 0) return;

    const url = `api/duty-schedule/excel`;
    const fd = new FormData();
    fd.append('excel', files[0]);

    try {
      const response = await getWindowFetcher({
        method: 'POST',
        accessToken,
        body: fd,
      })(url);

      const result = await response.json();

      // 오류
      if (result.status === 'FAIL') {
        setDialogContents({
          type: 'error',
          body: (
            <div
              style={{
                margin: '10px auto',
                textAlign: 'center',
              }}
            >
              <div>The file cannot be registered. Please check the file.</div>
              {result.message ? <div>{result.message}</div> : null}
            </div>
          ),
          footer: (
            <PrimaryButton
              onClick={async () => {
                setDialogContents({ type: 'hidden' });
              }}
            >
              Confirm
            </PrimaryButton>
          ),
        });

        return;
      }

      setDialogContents({
        type: 'success',
        body: (
          <div
            style={{
              margin: '10px auto',
              textAlign: 'center',
            }}
          >
            The schedule has been successfully registered.
          </div>
        ),
        footer: (
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
              getScheduleStaffData(accessToken);
            }}
          >
            Confirm
          </PrimaryButton>
        ),
      });
    } catch (err) {
      console.log(err);
    } finally {
      if (ref.current) ref.current.value = '';
    }
  };

  /**
   * on/off 핸들러
   */
  const enabledHandler = (value: boolean) => {
    if (!value) {
      setDialogContents({
        type: 'plain',
        body: `Are you sure you want to turn off? \n\r(You must click Submit for the changes to take effect.)`,
        footer: (
          <>
            <Sub1Button
              onClick={() => {
                setDialogContents({ type: 'hidden' });
              }}
            >
              {`No, I don't`}
            </Sub1Button>
            <PrimaryButton
              onClick={async () => {
                setSettings((prevState) => {
                  if (!prevState) return null;
                  return { ...prevState, enable: value };
                });
                setDialogContents({ type: 'hidden' });
              }}
            >
              Yes, turn off
            </PrimaryButton>
          </>
        ),
      });
      return;
    }

    setSettings((prevState) => {
      if (!prevState) return null;
      return { ...prevState, enable: value };
    });
  };

  /**
   * TODO: 이전 버전으로 되돌림 (Switch) - 배포시 제거 예정
   * @returns
   */
  // const radioHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!settings) return;

  //   const value = ev.target.value;
  //   const copy = { ...settings };

  //   if (value === 'OFF') {
  //     setDialogContents({
  //       type: 'plain',
  //       body: `Are you sure you want to turn off automatically report send? \n\r(You must click Submit for the changes to take effect.)`,
  //       footer: (
  //         <>
  //           <Sub1Button
  //             onClick={() => {
  //               setDialogContents({ type: 'hidden' });
  //             }}
  //           >
  //             {`No, I don't`}
  //           </Sub1Button>
  //           <PrimaryButton
  //             onClick={async () => {
  //               setSettings((prevState) => {
  //                 if (!prevState) return null;
  //                 return { ...prevState, enable: false };
  //               });
  //               setDialogContents({ type: 'hidden' });
  //             }}
  //           >
  //             Yes, turn off
  //           </PrimaryButton>
  //         </>
  //       ),
  //     });

  //     return;
  //   } else {
  //     copy.enable = true;
  //     copy.targetingMethod = value;
  //     setSettings(copy);
  //   }
  // };

  /**
   *
   * @returns
   */
  const changeMetaHandler = (metadata: any) => {
    if (!settings) return;

    const copy: NotificationSetting = {
      ...settings,
      targetingSpecifiedDicomTagId: metadata.id,
    };

    setMetaList({ ...metadata });
    setSettings(copy);
  };

  /**
   *
   * @returns
   */
  const resetMetaHandler = () => {
    if (!settings) return;

    const copy: NotificationSetting = {
      ...settings,
      targetingSpecifiedDicomTagId: '',
    };

    setSettings(copy);

    setMetaList({
      action: '',
      dataset: '',
      definition: '',
      dicomTagId: 0,
      method: '',
      tag: '',
    });
  };

  //
  const updateNotificationSettings = async () => {
    const url = 'api/setting/scs/notification';

    const notificationRecipients = recipients.map(
      ({ countryCallingCode, email, name, tel, id, designator, oncall, active, notificationId }) => ({
        countryCallingCode: countryCallingCode,
        email,
        name,
        tel,
        id,
        designator,
        oncall,
        active,
        notificationId,
      }),
    );

    const body = {
      ...settings,
      notificationRecipients,
    };

    const r = await getFetcher({
      method: 'POST',
      accessToken,
      body,
    })(url);

    const response = r as FetchResponse<any>;

    if (response.status === 'SUCCESS' && response.code === '200') {
      setDialogContents({
        type: 'plain',
        body: `It has been reflected.`,
        footer: (
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            OK
          </PrimaryButton>
        ),
      });

      mutate();
    } else {
      errorHandler(r);
    }
  };

  /**
   * 당직 인원 정보 get
   * @param accessToken
   */
  const getScheduleStaffData = async (accessToken: string) => {
    const response = await fetcher({
      url: `api/duty-schedule/staff`,
      accessToken,
    });

    setStaff([...(response.result as Staff[])]);
  };

  /**
   * SET token key in state
   */
  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) {
      setAccessToken(newToken);
      getScheduleStaffData(newToken);
    }
  }, []);

  /**
   * swr에 데이터가 update 되면 실행되는 observer
   */
  useEffect(() => {
    if (data && data.result) {
      const expanded =
        data.result.notificationRecipients &&
        data.result.notificationRecipients.map<ExpandRecipient>((props) => {
          return {
            ...props,
            isEdit: false,
            isComplete: true,
            tempId: UUID(),
          };
        });

      const disabledTargets: { [key: string]: boolean } = {};

      for (let key in data.result) {
        if (DISABLED_TARGETS.includes(key)) {
          const disabledTarget = (key as string).split('Date')[0] + 'Time';
          disabledTargets[disabledTarget] = data.result[key as keyof typeof data.result] === false;
        }
      }

      getDicomTagAll().then((response) => {
        setDicomTagList(response.result);

        if (data.result.targetingSpecifiedDicomTagId) {
          const selectedDicomTag = response.result.find(
            (item: any) => item.id === data.result.targetingSpecifiedDicomTagId,
          );

          if (selectedDicomTag) {
            const { definition, id, tag, vr } = selectedDicomTag as DicomTag;
            setMetaList((prevState) => ({
              ...prevState,
              dicomTagId: id,
              definition,
              tag,
              vr,
            }));
          }
        }
      });

      setDisableds((prevState) => {
        return {
          ...prevState,
          ...disabledTargets,
        };
      });

      if (expanded) {
        setRecipients(expanded);
        setRecipientsBackup(expanded);
      }

      setSettings(data.result);
      setBackup(data.result);
    }
  }, [data]);

  if (error) {
    return <b>error</b>;
  }

  if (!data) {
    return <b>Loading...</b>;
  }

  if (!data.result) {
    if (data.code === 'UNAUTHORIZED') {
      return <Navigate to={'/'} replace={true} />;
    }

    return (
      <div>
        <>Error : {data.code || 'unknown'} </>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <>
      <label>
        Notification
        <span className="sub-title">Select the required functions.</span>
      </label>

      {/*Notification*/}
      <StyledNotificationWrap>
        <StyledNotificationArea>
          <strong>Notification</strong>
          <StyledNotificationBoxFlex>
            <span>OFF</span>
            <SwitchAsync checked={settings.enable} onChangeValue={enabledHandler} />
            <span>ON</span>
          </StyledNotificationBoxFlex>
          {/* <StyledNotificationBox>
            
            <StyledRadioList>
              <li>
                <Input
                  type="radio"
                  name="notification"
                  value="OFF"
                  checked={settings.enable === false}
                  onChange={radioHandler}
                >
                  OFF
                </Input>
              </li>

              <li>
                <Input type="radio" name="notification" value="ALL" checked={isAll} onChange={radioHandler}>
                  Active ALL
                </Input>
              </li>

              <li>
                <Input type="radio" name="notification" value="SPECIFIED" checked={isSpecified} onChange={radioHandler}>
                  Condition Sending
                </Input>
              </li>
            </StyledRadioList>
          </StyledNotificationBox> */}
        </StyledNotificationArea>

        {settings.enable && (
          <>
            {/*Result*/}
            <StyledNotificationArea>
              <strong>Result</strong>
              <StyledNotificationBox>
                <StyledInputList>
                  <li>
                    <Checkbox checked disabled>
                      Only suspected
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      checked={settings.triggerNotSuspected}
                      name="triggerNotSuspected"
                      onChange={checkedHandler}
                    >
                      Including not suspected
                    </Checkbox>
                  </li>
                </StyledInputList>
              </StyledNotificationBox>
            </StyledNotificationArea>

            {/*Type*/}
            <StyledNotificationArea>
              <strong>Type</strong>
              <StyledNotificationBox>
                <StyledInputList>
                  <li>
                    <Checkbox checked={settings.sendSms} name="sendSms" onChange={checkedHandler}>
                      SMS
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox checked={settings.sendEmail} name="sendEmail" onChange={checkedHandler}>
                      E-Mail
                    </Checkbox>
                  </li>
                </StyledInputList>
              </StyledNotificationBox>
            </StyledNotificationArea>

            {/*Contents*/}
            <StyledNotificationArea>
              <strong>Contents</strong>
              <StyledNotificationBox>
                <StyledInputList>
                  <li>
                    <Checkbox checked disabled>
                      Name
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox checked disabled>
                      Result
                    </Checkbox>
                  </li>

                  <li>
                    <Checkbox checked={settings.contentPatientId} name="contentPatientId" onChange={checkedHandler}>
                      Patient ID
                    </Checkbox>
                  </li>

                  <li>
                    <Checkbox checked={settings.contentPatientSex} name="contentPatientSex" onChange={checkedHandler}>
                      Gender
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox checked={settings.contentPatientAge} name="contentPatientAge" onChange={checkedHandler}>
                      Age
                    </Checkbox>
                  </li>
                </StyledInputList>
                <StyledInputList>
                  <li>
                    <Checkbox
                      checked={settings.contentAcquisitionDate}
                      name="contentAcquisitionDate"
                      onChange={checkedHandler}
                    >
                      Acquisition Date
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      checked={settings.contentAcquisitionTime}
                      name="contentAcquisitionTime"
                      disabled={disableds.contentAcquisitionTime}
                      onChange={checkedHandler}
                    >
                      Acquisition Time
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      checked={settings.contentAnalysisStartDate}
                      name="contentAnalysisStartDate"
                      onChange={checkedHandler}
                    >
                      Analysis Start Date
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      checked={settings.contentAnalysisStartTime}
                      name="contentAnalysisStartTime"
                      disabled={disableds.contentAnalysisStartTime}
                      onChange={checkedHandler}
                    >
                      Analysis Start Time
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      checked={settings.contentAnalysisEndDate}
                      name="contentAnalysisEndDate"
                      onChange={checkedHandler}
                    >
                      Analysis End Date
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      checked={settings.contentAnalysisEndTime}
                      name="contentAnalysisEndTime"
                      disabled={disableds.contentAnalysisEndTime}
                      onChange={checkedHandler}
                    >
                      Analysis End Time
                    </Checkbox>
                  </li>
                </StyledInputList>
              </StyledNotificationBox>
            </StyledNotificationArea>

            {/*Specified Metatag */}
            <StyledNotificationArea>
              <strong>Metatag</strong>

              <StyledNotificationBox>
                <div className="metatag-area">
                  <SelectSearchDicomTag
                    dicomTagList={dicomTagList}
                    value={metaList}
                    dataIdx={0}
                    onChangeFunc={changeMetaHandler}
                    resetChangeFunc={resetMetaHandler}
                  />
                </div>
              </StyledNotificationBox>
            </StyledNotificationArea>

            {/*Active&Specified Recipients*/}
            <StyledNotificationArea>
              <strong>Recipients</strong>

              <Recipient
                recipients={recipients}
                countryCallingCode={code.tel}
                onUpdateRecipients={recipientsUpdateHandler}
              />
            </StyledNotificationArea>

            {/*Schedule Specified*/}
            <StyledNotificationArea>
              <strong>
                Schedule
                <br />
                Recipients
              </strong>
              <ScheduleRecipient staff={staff} />
            </StyledNotificationArea>

            {/*Schedule*/}
            <StyledNotificationArea>
              <strong>Schedule</strong>
              <StyledNotificationBox>
                <StyledExcelRow>
                  <span className="tit">① Excel download</span>
                  <div>
                    <Select
                      styles={css`
                        width: 100px;
                      `}
                      selected={year}
                      list={Array(Number(year) - 2025 + 1)
                        .fill(undefined)
                        .map((_, i) => ({
                          label: `${2025 + i}`,
                          value: `${2025 + i}`,
                        }))}
                      onSelected={(value) => setYear(value.label)}
                    />
                  </div>
                  <div>/</div>
                  <div>
                    <Select
                      styles={css`
                        width: 70px;
                      `}
                      selected={month}
                      list={Array(12)
                        .fill(undefined)
                        .map((_, i) => ({
                          label: `${i + 1}`,
                          value: `${i + 1}`,
                        }))}
                      onSelected={(value) => setMonth(value.label)}
                    />
                  </div>
                  <StyledExcelButton onClick={downloadExcelHandler}>Download</StyledExcelButton>
                </StyledExcelRow>

                <StyledExcelRow>
                  <span className="tit">② Excel upload</span>
                  <StyledExcelButton
                    onClick={() => {
                      ref.current?.click();
                    }}
                  >
                    <input ref={ref} type="file" onChange={uploadExcelHandler} />
                    <span>Upload</span>
                  </StyledExcelButton>
                </StyledExcelRow>
              </StyledNotificationBox>
            </StyledNotificationArea>
          </>
        )}
      </StyledNotificationWrap>
      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            margin-top: 8px;
            font-size: 18px;
          `}
          onClick={submitDebounce(updateNotificationSettings)}
          disabled={isMutate}
        >
          Apply
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default NotificationPage;
